/**
 * Ranges of a number: the rows of a number table, and the columns of a
 * number in a banner. A range holds the values from `min` to `max`, both
 * inclusive; a bound left out is open. Pure.
 */
import { refusedName } from "../compiler/names"
import { boundsProblem } from "./conditions"

/** A range as the editors and the compiler read it: its name and its bounds. */
export interface RangeItem {
  name: string
  min?: number
  max?: number
}

/** The name a new range gets from its bounds: "10 to 20", "10 or more", "up to 20"; "" without a bound. */
export const rangeName = (min?: number, max?: number): string => {
  if (min !== undefined && max !== undefined) return `${min} to ${max}`
  if (min !== undefined) return `${min} or more`
  if (max !== undefined) return `up to ${max}`
  return ""
}

/** The most ranges "Add equal ranges" makes at once. */
export const MOST_EQUAL_RANGES = 100

/**
 * Ranges of whole numbers from `from` to `to`, each ending `step` above the
 * last: the first from `from` to the first multiple of `step` above it, each
 * next from one above the last one's end, the last ending at `to`. From 0
 * to 100 by 20: 0 to 20, 21 to 40, 41 to 60, 61 to 80, 81 to 100. Refused,
 * with the reason, for a number that is not whole, a step below 1, `from`
 * above `to`, and more than `MOST_EQUAL_RANGES` ranges.
 */
export const equalRanges = (from: number, to: number, step: number): RangeItem[] | string => {
  if (![from, to, step].every(Number.isSafeInteger))
    return "Equal ranges need whole numbers. Add each range on its own instead."
  if (step < 1) return "The step must be 1 or more."
  if (from > to) return "The from value is above the to value."
  const out: RangeItem[] = []
  let min = from
  let max = Math.min((Math.floor(from / step) + 1) * step, to)
  while (min <= to) {
    if (out.length === MOST_EQUAL_RANGES)
      return `That makes more than ${MOST_EQUAL_RANGES} ranges. Choose a larger step.`
    out.push({ name: rangeName(min, max), min, max })
    min = max + 1
    max = Math.min(max + step, to)
  }
  return out
}

/**
 * Why `range` cannot be added to a list whose other names are `others`, or
 * null when it can: its name, by the rule for the names of rows and columns
 * (`refusedName`), then its bounds (`boundsProblem`).
 */
export const rangeRefusal = (
  range: RangeItem,
  others: string[],
  owner: "row" | "column" = "row"
): string | null => {
  const refused = refusedName(range.name, others, owner)
  if (refused) return refused
  const problem = boundsProblem(range.min, range.max)
  return problem ? `The range ${problem}.` : null
}

const compare = (a: number, b: number): number => (a < b ? -1 : a > b ? 1 : 0)

/**
 * What a list of ranges should be warned of: each two that overlap ("The
 * ranges "0 to 20" and "15 to 30" overlap."), in list order; then, sorted by
 * their from values, each gap between them ("No range holds values from 21
 * to 24."). A gap is said only where the range below ends on a whole number
 * and the one above starts on one. A range whose bounds cannot be used
 * (`boundsProblem`) is left out.
 */
export const rangeWarnings = (ranges: RangeItem[]): string[] => {
  const usable = ranges.filter((r) => boundsProblem(r.min, r.max) === null)
  const lo = (r: RangeItem): number => r.min ?? -Infinity
  const hi = (r: RangeItem): number => r.max ?? Infinity
  const out: string[] = []
  usable.forEach((a, i) =>
    usable.slice(i + 1).forEach((b) => {
      if (Math.max(lo(a), lo(b)) <= Math.min(hi(a), hi(b)))
        out.push(`The ranges "${a.name.trim()}" and "${b.name.trim()}" overlap.`)
    })
  )
  const sorted = [...usable].sort((a, b) => compare(lo(a), lo(b)) || compare(hi(a), hi(b)))
  // The highest value the ranges so far hold.
  let reach = -Infinity
  sorted.forEach((r) => {
    const from = r.min
    if (Number.isInteger(reach) && from !== undefined && Number.isInteger(from) && from > reach + 1)
      out.push(
        from - 1 === reach + 1
          ? `No range holds the value ${reach + 1}.`
          : `No range holds values from ${reach + 1} to ${from - 1}.`
      )
    reach = Math.max(reach, hi(r))
  })
  return out
}
